/**
 * Component for refreshing the session on a set interval.
 */
import React from 'react';
import PropTypes from 'prop-types';
import MorePropTypes from 'airbnb-prop-types';

const propTypes = {
  isAuthenticated: PropTypes.func.isRequired,
  updateSessionHandler: PropTypes.func.isRequired,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  expirationDate: MorePropTypes.or([MorePropTypes.explicitNull, PropTypes.number]).isRequired,
  startDate: MorePropTypes.or([MorePropTypes.explicitNull, PropTypes.number]).isRequired,
};

const defaultProps = {
  children: null,
};

class UpdateSession extends React.Component {
  static getDerivedStateFromProps(props, state) {
    const { isAuthenticated, updateSessionHandler, startDate, expirationDate } = props;
    const { running, intervalId } = state;
    let newState = state;
    if (isAuthenticated() && !running) {
      newState = UpdateSession.calculateAndSetInterval(
        startDate,
        expirationDate,
        updateSessionHandler,
      );
    } else if (!isAuthenticated() && running) {
      clearInterval(intervalId);
      newState = {
        running: false,
        intervalId: null,
      };
    }
    return newState;
  }

  /**
   * Calculates interval. If the interval calculated is past the expiration
   * update the session.
   *
   * @param startDate {number} - The timestamp when the token was issued (in ms).
   * @param expirationDate {number} - The timestamp when the token expires (in ms).
   * @param updateSessionHandler {func} - The handler to update the session.
   *
   * @returns {object} - New state for component.
   */
  static calculateAndSetInterval(startDate, expirationDate, updateSessionHandler) {
    const updateTime = UpdateSession.getIntervalInMilliseconds(startDate, expirationDate);
    // If the calculated refesh time is after the token expiration update the session now.
    const currentTimeInMS = new Date().getTime();
    if (currentTimeInMS + updateTime >= expirationDate) {
      updateSessionHandler();
      return {
        running: false,
        intervalId: null,
      };
    }
    return {
      running: true,
      intervalId: setInterval(updateSessionHandler, updateTime),
    };
  }

  /**
   * Calculates the time to refresh in milliseconds based off expiration time.
   *
   * @param sessionStart {number} - Timestamp, in milliseconds, the session was initialized.
   * @param sessionExpiration {number} - Timestamp, in milliseconds, the session will expire.
   *
   * @returns {number} - The update interval in ms.
   */
  static getIntervalInMilliseconds(sessionStart, sessionExpiration) {
    const sessionInterval = sessionExpiration - sessionStart;
    const intervalDifference = sessionInterval / 3;
    const updateIntervalInMilliSeconds = sessionInterval - intervalDifference;
    return updateIntervalInMilliSeconds;
  }

  constructor(props) {
    super(props);
    this.state = {
      running: false,
      intervalId: null,
    };
  }

  componentDidMount() {
    const { isAuthenticated, updateSessionHandler, startDate, expirationDate } = this.props;
    const { running } = this.state;
    if (isAuthenticated() && !running) {
      this.setState(
        UpdateSession.calculateAndSetInterval(startDate, expirationDate, updateSessionHandler),
      );
    }
  }

  componentWillUnmount() {
    const { running, intervalId } = this.state;
    if (running) {
      clearInterval(intervalId);
      this.setState({
        running: false,
        intervalId: null,
      });
    }
  }

  render() {
    const { children } = this.props;
    return children;
  }
}

UpdateSession.propTypes = propTypes;
UpdateSession.defaultProps = defaultProps;

export default UpdateSession;
